interface ApiErrorBody {
  error?: string;
  details?: {
    fieldErrors?: Record<string, string[]>;
    formErrors?: string[];
  };
}

function errorMessage(body: ApiErrorBody): string {
  const fieldMessage = body.details?.fieldErrors
    ? Object.values(body.details.fieldErrors).flat().find(Boolean)
    : undefined;
  return fieldMessage || body.details?.formErrors?.[0] || body.error || 'Operazione non riuscita';
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

export interface ApiOptions extends RequestInit {
  /** Nuovi tentativi se la connessione cade (03/11/2026, «Failed to fetch» su Narciso). Solo GET li ha di default. */
  retries?: number;
  /** Tempo massimo per ogni tentativo. */
  timeoutMs?: number;
  /** Avvisa chi chiama che la richiesta è stata ripetuta. */
  onRetry?: (attempt: number) => void;
}

export const NETWORK_ERROR_MESSAGE =
  'La connessione con il gestionale si è interrotta e non sono riuscito a completare l’operazione. Controlla internet e riprova: quello che è già salvato non si perde.';

let writesInFlight = 0;
/** Quante scritture (non GET) sono ancora in viaggio: l'aggiornamento automatico aspetta che finiscano. */
export function pendingWrites(): number {
  return writesInFlight;
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Solo le letture si ripetono da sole: le scritture lo chiedono esplicitamente, perché alcune toccano Stripe,
// Instagram o cancellano dati e ripeterle può creare doppioni o errori finti.
function defaultRetries(method: string): number {
  return method === 'GET' ? 2 : 0;
}

export async function api<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const { retries, timeoutMs, onRetry, ...init } = options;
  const method = (init.method || 'GET').toUpperCase();
  const attempts = 1 + (retries ?? defaultRetries(method));
  const isFormData = typeof FormData !== 'undefined' && init.body instanceof FormData;
  if (method !== 'GET') writesInFlight += 1;
  try {
    return await send<T>(path, init, attempts, isFormData, timeoutMs, onRetry);
  } finally {
    if (method !== 'GET') writesInFlight -= 1;
  }
}

async function send<T>(path: string, init: RequestInit, attempts: number, isFormData: boolean, timeoutMs?: number, onRetry?: (attempt: number) => void): Promise<T> {
  for (let attempt = 1; ; attempt += 1) {
    // AbortController + setTimeout invece di AbortSignal.timeout: funziona anche su iPad/iPhone con iOS 15.
    const controller = timeoutMs && !init.signal ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : undefined;
    try {
      const response = await fetch(path, {
        credentials: 'same-origin',
        ...init,
        signal: init.signal || controller?.signal,
        headers: init.body && !isFormData ? { 'Content-Type': 'application/json', ...init.headers } : init.headers,
      });
      const text = await response.text();
      let body = {} as T & ApiErrorBody;
      try { body = text ? JSON.parse(text) : body; } catch {
        // Risposta tagliata a metà: non è un successo.
        if (response.ok) throw new TypeError('Risposta incompleta');
      }
      if (!response.ok) throw new ApiError(errorMessage(body), response.status);
      return body;
    } catch (cause) {
      if (cause instanceof ApiError || init.signal?.aborted) throw cause;
      if (attempt >= attempts) throw new ApiError(NETWORK_ERROR_MESSAGE, 0);
      onRetry?.(attempt + 1);
      await sleep(700 * attempt * attempt);
    } finally {
      if (timer) clearTimeout(timer);
    }
  }
}

export function payload(method: string, body?: unknown): RequestInit {
  return { method, body: body === undefined ? undefined : JSON.stringify(body) };
}
